import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { LayoutService, ScreenSize } from 'libs/portal/src/layout/layout.service';
import { User } from 'libs/portal/src/models/User';
import { GenericService } from 'libs/portal/src/services/generic/generic.service';
import { ToastrService } from 'ngx-toastr';
import { UserRightsService } from 'libs/portal/src/services/user-rights/user-rights.service';
import { PasswordValidator } from 'libs/portal/src/validators/password-validator';
import { Endpoints } from '@huge-monorepo/portal/config';
import { Routes } from '@huge-monorepo/portal/config';

export interface UserInfo {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
}

@Component({
  selector: 'app-profile-page',
  templateUrl: './profile-page.component.html',
  styleUrls: ['./profile-page.component.scss'],
})
export class ProfilePageComponent implements OnInit {
  secretSource: Blob;
  secretSourceURL: any;
  secretHex: string;
  otp = new UntypedFormControl('');
  user: User;
  userToUpdate: UserInfo;
  isLoaded = false;
  ScreenSize = ScreenSize;

  otpForm = new UntypedFormGroup({
    otpControl: new UntypedFormControl('', []),
  });

  passwordControl = new UntypedFormControl('', [Validators.required]);

  formGroup: UntypedFormGroup = new UntypedFormGroup(
    {
      passwordControl: new UntypedFormControl('', Validators.compose([Validators.minLength(8), Validators.required])),
      confirmPasswordControl: new UntypedFormControl('', [Validators.required]),
    },
    (formGroup: any) => {
      return PasswordValidator.areEqual(formGroup);
    },
  );

  validation_messages = {
    passwordControl: [
      { type: 'required', message: 'Password is required.' },
      {
        type: 'minLength',
        message: 'Please enter a password with more than 8 characters.',
      },
    ],
    matching_passwords: [{ type: 'areEqual', message: 'Password do not match.' }],
    confirm_password: [{ type: 'required', message: 'Confirm password is required.' }],
  };

  constructor(
    private genericService: GenericService,
    private router: Router,
    private auth: UserRightsService,
    private toastService: ToastrService,
    public layoutService: LayoutService,
    private endpoints: Endpoints,
    private routes: Routes,
  ) {}

  ngOnInit() {
    this.genericService.GET(this.endpoints.GET_PROFILE_USER(this.auth.GetUID())).subscribe((user) => {
      const userData = user['body'];
      this.user = <User>userData;
      this.userToUpdate = {
        id: userData.ID,
        firstName: userData.Name,
        lastName: userData.Surname,
        email: userData.Email,
      };
      this.isLoaded = true;
    });
  }

  userUpdated() {
    this.toastService.success('', 'User successfully updated', { timeOut: 4000 });
    this.router.navigate([this.routes.Home]);
  }

  activateOTP() {
    this.genericService.GET(this.endpoints.ENABLE_OTP, true, 'blob').subscribe(
      (results) => {
        const hexKey = results['headers'].get('X-Huge-Otp');
        this.user.OTPEnabled = true;
        this.secretSource = new Blob([results['body']], {
          type: results['headers'].get('content-type'),
        });
        this.createImageFromBlob(this.secretSource);
        this.secretHex = hexKey;
        this.toastService.success('', 'Activated: 2 Factor Auth!', { timeOut: 3000 });
      },
      (err) => {
        console.log(err);
      },
    );
  }

  disableOTP() {
    this.genericService.GET(this.endpoints.DISABLE_OTP).subscribe(
      () => {
        this.user.OTPEnabled = false;
        this.secretSource = null;
        this.toastService.success('', 'Disabled: 2 Factor Auth!', { timeOut: 3000 });
      },
      (err) => {
        console.log(err);
      },
    );
  }

  createImageFromBlob(image: Blob) {
    const reader = new FileReader();
    reader.addEventListener(
      'load',
      () => {
        this.secretSourceURL = reader.result;
      },
      false,
    );
    if (image) {
      reader.readAsDataURL(image);
    }
  }

  renewOTP() {
    this.genericService.GET(this.endpoints.ENABLE_OTP, true, 'blob').subscribe((results: any) => {
      const hexKey = results['headers'].get('X-Huge-Otp');
      this.secretSource = new Blob([results['body']], {
        type: results['headers'].get('content-type'),
      });
      this.createImageFromBlob(this.secretSource);
      this.secretHex = hexKey;
      this.toastService.success('', 'Renewed: 2 Factor Auth!', { timeOut: 3000 });
    });
  }

  validateOTP() {
    const otpControl = this.otpForm.get('otpControl');
    if (otpControl.value != null && otpControl.value != '') {
      this.genericService.GET(this.endpoints.VALIDATE_OTP, true, 'json', otpControl.value).subscribe((results: any) => {
        if (results['body'] == null) {
          this.toastService.success('', 'OTP Validated: 2 Factor Auth!', { timeOut: 3000 });
        }
      });
    } else {
      otpControl.setErrors({ invalid: true });
      otpControl.markAsTouched();
    }
  }

  resetPassword() {
    if (this.formGroup.status == 'INVALID') {
      this.formGroup.get('passwordControl').setErrors({ passwordMismatch: true });
    }
    if (
      this.formGroup.get('passwordControl').value == '' ||
      this.formGroup.get('confirmPasswordControl').value == '' ||
      this.formGroup.get('passwordControl').errors != null ||
      this.formGroup.get('confirmPasswordControl').errors != null
    ) {
      this.formGroup.get('passwordControl').markAsTouched();
      this.formGroup.get('confirmPasswordControl').markAsTouched();
    } else {
      this.genericService
        .PUT(this.endpoints.RESET_PASSWORD(this.auth.GetUID()), {
          password: this.formGroup.get('passwordControl').value,
        })
        .subscribe(() => {
          this.toastService.success('', 'Password successfully updated', { timeOut: 4000 });
        });
    }
  }
}
